import { isPortalOgsmEnabled } from '@/configs/feature-flags'
import {
  PORTAL_OGSM_EDIT_CAPABILITY,
  PORTAL_OGSM_VIEW_CAPABILITY,
} from '@/features/ogsm/access'
import type { OgsmApi } from '@/features/ogsm/ogsm-api'
import {
  addPortalOgsmActual,
  archivePortalOgsmNode,
  createPortalOgsmNode,
  createPortalOgsmSession,
  deletePortalOgsmNode,
  getPortalOgsmBoard,
  getPortalOgsmRemovalImpact,
  listPortalOgsmActuals,
  listPortalOgsmAssignees,
  listPortalOgsmSessions,
  movePortalOgsmNode,
  revisePortalOgsmTarget,
  updatePortalOgsmNode,
  updatePortalOgsmStatement,
} from '@/services/portal.service'
import { selectCapabilities, useUserStore } from '@/stores/user.store'

/**
 * The portal's OGSM data scope: only `/portal/company/ogsm/*`, no Scorecard to
 * refresh, and no metric linking (there is no portal metric source, G2).
 */
export const PORTAL_OGSM_API: OgsmApi = {
  // A literal, not portalKeys: portal route tests mock `use-portal` wholesale.
  key: ['portal', 'ogsm'],
  scorecardKey: null,
  metricLinking: false,
  listSessions: listPortalOgsmSessions,
  createSession: createPortalOgsmSession,
  getBoard: getPortalOgsmBoard,
  updateStatement: updatePortalOgsmStatement,
  createNode: createPortalOgsmNode,
  updateNode: updatePortalOgsmNode,
  deleteNode: deletePortalOgsmNode,
  getRemovalImpact: getPortalOgsmRemovalImpact,
  archiveNode: archivePortalOgsmNode,
  moveNode: movePortalOgsmNode,
  listAssignees: listPortalOgsmAssignees,
  listActuals: listPortalOgsmActuals,
  addActual: addPortalOgsmActual,
  reviseTarget: revisePortalOgsmTarget,
}

/** UI gate only: the backend's `/portal/company/ogsm/*` guards are the real check. */
export function usePortalOgsmAccess(): { canView: boolean; canEdit: boolean } {
  // Booleans, not the array: with no profile `selectCapabilities` returns a new [] each call.
  const hasView = useUserStore((state) =>
    selectCapabilities(state).includes(PORTAL_OGSM_VIEW_CAPABILITY)
  )
  const hasEdit = useUserStore((state) =>
    selectCapabilities(state).includes(PORTAL_OGSM_EDIT_CAPABILITY)
  )
  const canView = isPortalOgsmEnabled() && hasView
  return { canView, canEdit: canView && hasEdit }
}
